import type { DrawingContext } from "../../lib/drawingContext";
import {
  pickedElementFromPreview,
  type PickedElementContextDraft,
} from "../../lib/pickedElementContext";
import type {
  AgentBrowserSitePolicy,
  DesktopLocalServer,
  DesktopPreviewAnnotationMode,
  DesktopPreviewPickedElement,
  DesktopPreviewPickMode,
  ProjectId,
  ScopedThreadRef,
} from "@threadlines/contracts";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CameraIcon,
  CheckIcon,
  ExternalLinkIcon,
  GlobeIcon,
  MaximizeIcon,
  MinimizeIcon,
  ChevronDownIcon,
  EraserIcon,
  MousePointerClickIcon,
  PencilIcon,
  SquareDashedMousePointerIcon,
  MoreVerticalIcon,
  PlusIcon,
  RadioTowerIcon,
  RotateCwIcon,
  XIcon,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import { scopedThreadKey } from "@threadlines/client-runtime";
import { IDLE_NAV_STATE, useBrowserLiveStore } from "../../browserLiveStore";
import { BROWSER_SLOT_ANCHOR, claimThreadBrowser, closeThreadBrowser } from "./BrowserHostLayer";
import type { PreviewWebview } from "./PreviewTabFrame";

import {
  BROWSER_VIEWPORT_PRESETS,
  RESPONSIVE_VIEWPORT,
  callWhenReady,
  getPreviewWebview,
  selectActiveTab,
  selectPendingBrowserApprovals,
  selectThreadAgentState,
  selectThreadBrowserOwnership,
  selectThreadBrowserState,
  steppedZoom,
  useBrowserPanelStore,
  type BrowserAppearance,
  type BrowserTab,
  type BrowserViewport,
} from "../../browserPanelStore";
import { BrowserFindBar } from "./BrowserFindBar";
import { BrowserPageErrorsButton } from "./BrowserPageErrors";
import { distillPageErrors, type PageErrorItem } from "./pageErrors";
import { isElectron } from "../../env";
import { useBrowserProfilePartition } from "../../lib/browserProfiles";
import { useTheme } from "../../hooks/useTheme";
import { copyTextToClipboard } from "../../lib/clipboard";
import { cn } from "../../lib/utils";
import {
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuSub,
  MenuSubPopup,
  MenuSubTrigger,
  MenuTrigger,
} from "../ui/menu";
import { MINI_HORIZONTAL_SCROLLBAR_CLASS, ScrollArea } from "../ui/scroll-area";
import { toastManager } from "../ui/toast";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { RotateDeviceIcon } from "../Icons";
import { pushNavigationPolicy, useBrowserApprovals } from "./browserApprovals";
import { answerBrowserApproval, type BrowserApprovalDecision } from "./browserApprovalRequests";
import { noteBrowserUserIntent } from "./browserFocusGuard";
import { completeUrl } from "./urlCompletion";
import { type AgentActivity } from "./previewAutomationHost";
import { hostOf, normalizePreviewUrl } from "./previewUrl";

export function BrowserPanel({
  threadRef,
  projectId = null,
  flexGrow,
  onClose,
  onPickElement,
  onScreenshot,
  onDrawing,
  onAttachPageErrors,
  pendingReveal,
  onRevealHandled,
}: {
  threadRef: ScopedThreadRef;
  /** The thread's project, for approvals while the thread is still a local draft. */
  projectId?: ProjectId | null;
  /** Complement of the chat column's share, so the two honour one ratio. */
  flexGrow: number;
  onClose: () => void;
  /** Hands a picked element to the composer as context for the next message. */
  /** groupId ties the elements of one multi-element attach together; null on
   *  a lone pick. */
  onPickElement?:
    | ((element: DesktopPreviewPickedElement, groupId: string | null) => void)
    | undefined;
  /** Attaches a captured screenshot to the message being written. Returns
   *  whether it actually attached, so the panel confirms only what happened. */
  onScreenshot?: ((input: { dataUrl: string; name: string }) => boolean) | undefined;
  /** Attaches a drawing made on the page: one chip carrying its picture. */
  onDrawing?: ((context: DrawingContext) => void) | undefined;
  /** Attaches the page's reported errors to the message as a text file. */
  onAttachPageErrors?: ((attachment: { name: string; text: string }) => void) | undefined;
  /** An element to show again, requested from a composer chip. */
  pendingReveal?: PickedElementContextDraft | null | undefined;
  onRevealHandled?: (() => void) | undefined;
}) {
  const browserState = useBrowserPanelStore((store) =>
    selectThreadBrowserState(store.browserStateByThreadKey, threadRef),
  );
  const browserOwnership = useBrowserPanelStore((store) =>
    selectThreadBrowserOwnership(store.browserOwnershipByThreadKey, threadRef),
  );
  const setTabUrl = useBrowserPanelStore((store) => store.setTabUrl);
  const setTabViewport = useBrowserPanelStore((store) => store.setTabViewport);
  const openTab = useBrowserPanelStore((store) => store.openTab);
  const closeTab = useBrowserPanelStore((store) => store.closeTab);
  const selectTab = useBrowserPanelStore((store) => store.selectTab);
  const markBrowserUserControlled = useBrowserPanelStore(
    (store) => store.markBrowserUserControlled,
  );
  const expanded = useBrowserPanelStore((store) => store.expanded);
  const toggleExpanded = useBrowserPanelStore((store) => store.toggleExpanded);
  const deviceToolbarOpen = useBrowserPanelStore((store) => store.deviceToolbarOpen);
  const toggleDeviceToolbar = useBrowserPanelStore((store) => store.toggleDeviceToolbar);
  const appearance = useBrowserPanelStore((store) => store.appearance);
  const setAppearance = useBrowserPanelStore((store) => store.setAppearance);
  const setTabZoom = useBrowserPanelStore((store) => store.setTabZoom);
  const pendingApprovals = useBrowserPanelStore((store) =>
    selectPendingBrowserApprovals(store.pendingApprovalsByThreadKey, threadRef),
  );
  const pendingApproval = pendingApprovals[0] ?? null;
  const {
    approveHost,
    setProjectPolicy,
    removeHost,
    projectPolicy,
    defaultPolicy,
    access,
    projectId: approvalsProjectId,
  } = useBrowserApprovals(threadRef, projectId);
  const { resolvedTheme } = useTheme();
  // Each project browses in its own profile. Tabs are not rendered until it is
  // known: a webview's partition is fixed when it attaches.
  const profile = useBrowserProfilePartition(threadRef.environmentId, projectId);
  const partition = profile.status === "ready" ? profile.partition : null;

  const guestColorScheme = appearance === "system" ? resolvedTheme : appearance;

  const activeTab = selectActiveTab(browserState);
  const activeTabId = activeTab?.id ?? "";

  // The pages themselves live in the app-wide browser layer, which draws them
  // over this panel's page area while it is on screen and keeps them running
  // when it is not. The elements are in a registry for everything here that
  // talks to a page.
  const webviewFor = useCallback(
    (tabId: string) => getPreviewWebview(threadRef, tabId) as PreviewWebview | null,
    [threadRef],
  );
  const navState = useBrowserLiveStore(
    (store) => store.navStateByThreadKey[scopedThreadKey(threadRef)] ?? IDLE_NAV_STATE,
  );
  const overlayElement = useBrowserLiveStore((store) => store.overlayElement);
  const [addressDraft, setAddressDraft] = useState(activeTab?.url ?? "");
  const visitedUrls = useBrowserPanelStore((store) => store.visitedUrls);
  const activeUrl = activeTab?.url ?? null;

  // The address bar is an input the user types in, so it is only reset when the
  // page moves or the tab changes -- never on every keystroke. Back, forward
  // and loading come from the page itself, which reports them as it comes on
  // screen.
  useEffect(() => {
    setAddressDraft(activeUrl ?? "");
  }, [activeUrl, activeTabId]);

  /**
   * This panel is the thread's browser on screen while it is mounted: its
   * pages start if they were not running, the layer draws them here, and the
   * environment routes the thread's agent requests to this client.
   *
   * Leaving the screen keeps them running -- a thread switch, Settings -- unless
   * the panel was closed, which closes the pages too.
   */
  useEffect(() => {
    if (projectId !== null) {
      useBrowserLiveStore.getState().ensureLive(threadRef, projectId);
    }
    useBrowserLiveStore.getState().setShown(threadRef);
    claimThreadBrowser(threadRef);
    return () => {
      const live = useBrowserLiveStore.getState();
      if (live.shownThreadKey === scopedThreadKey(threadRef)) {
        live.setShown(null);
      }
      const stillOpen = selectThreadBrowserState(
        useBrowserPanelStore.getState().browserStateByThreadKey,
        threadRef,
      ).open;
      if (!stillOpen) {
        closeThreadBrowser(threadRef);
      }
    };
  }, [projectId, threadRef]);

  // Which tab is the agent's and what it is doing there, so it is visible
  // working rather than silently changing things. Produced by the automation
  // mount, which runs whether or not this panel is here. Its pointer is drawn
  // with the page it touched, by that page's frame.
  const agentState = useBrowserPanelStore((store) =>
    selectThreadAgentState(store.agentStateByThreadKey, threadRef),
  );
  const setAgentTab = useBrowserPanelStore((store) => store.setAgentTab);
  const setAgentActivity = useBrowserPanelStore((store) => store.setAgentActivity);
  const agentTabId = agentState.tabId;
  const agentActivity = agentState.activity as AgentActivity | null;

  const activeWebview = () => webviewFor(activeTabId) ?? null;

  // Host rather than hostname: on a dev box every page is localhost and the
  // port is the only thing telling two captures apart.
  const screenshotName = useCallback(() => {
    try {
      return new URL(activeUrl ?? "").host.replace(":", "-") || "page";
    } catch {
      return "page";
    }
  }, [activeUrl]);

  /*
   * The agent keeps its tab. Opening a tab, or switching to one, is yours to do
   * and does not move it.
   *
   * This was briefly the opposite -- an idle agent handed its tab over to
   * whichever one you were looking at -- and that was wrong twice over. "Idle"
   * was measured per operation rather than per turn, so opening a tab in the
   * gap between two of the agent's own actions stole the page mid-task. And it
   * was solving a problem that no longer exists: the agent could not see other
   * tabs when I wrote it, so following you was the only way it could ever end
   * up somewhere useful. It can see them now, and move itself, and say so when
   * it does.
   *
   * Which leaves the arrangement people expect from tabs. Yours are yours to
   * browse; its tab is marked in the strip and one click away from the activity
   * line, and it goes only where it decides to go.
   */

  useEffect(() => {
    // The agent's tab has been closed. Letting go here is what makes its next
    // action pick up the tab the user is actually looking at.
    if (agentTabId !== null && !browserState.tabs.some((tab) => tab.id === agentTabId)) {
      setAgentTab(threadRef, null);
      setAgentActivity(threadRef, null);
    }
  }, [agentTabId, browserState.tabs, setAgentActivity, setAgentTab, threadRef]);

  // The page area: the layer anchors the visible page to it, and pages kept
  // running off screen keep its size.
  const viewportAreaRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const element = viewportAreaRef.current;
    if (element === null) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry !== undefined && entry.contentRect.width > 0 && entry.contentRect.height > 0) {
        useBrowserLiveStore.getState().setSlotSize({
          width: Math.round(entry.contentRect.width),
          height: Math.round(entry.contentRect.height),
        });
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const addressRef = useRef<HTMLInputElement | null>(null);
  const deletingRef = useRef(false);

  const completeAddress = useCallback(
    (typed: string) => {
      setAddressDraft(typed);
      if (deletingRef.current) {
        return;
      }
      const completion = completeUrl(typed, visitedUrls);
      if (completion === null) {
        return;
      }
      // The completion goes in the field with the part you did not type
      // selected, so carrying on typing overwrites it and Enter accepts it.
      // This is what every address bar does, and the reason it never feels
      // like the field is arguing with you.
      setAddressDraft(completion);
      requestAnimationFrame(() => {
        addressRef.current?.setSelectionRange(typed.length, completion.length);
      });
    },
    [visitedUrls],
  );

  /**
   * Going somewhere yourself is approval.
   *
   * Typing an address, or picking a local server, is the user saying where the
   * browser should be -- so the site is recorded for the project without a
   * prompt. Being asked to approve the page you just asked for would read as
   * the app not listening.
   */
  const openAddress = useCallback(
    (tabId: string, url: string) => {
      const approved = approveHost(hostOf(url));
      setTabUrl(threadRef, tabId, url);
      const webview = webviewFor(tabId);
      // Armed before the page loads rather than on the next render: a site that
      // redirects on arrival would otherwise be judged against the allowlist as
      // it stood a moment before the user approved it.
      const webContentsId =
        webview === null ? null : callWhenReady(() => webview.getWebContentsId());
      if (webContentsId !== null) {
        pushNavigationPolicy(webContentsId, approved);
      }
      // Swallowed rather than surfaced: a page that redirects on arrival
      // aborts the load it interrupts, and a load the navigation guard refuses
      // already asks its question through the approval bar. Left unhandled,
      // either one dumps a GUEST_VIEW_MANAGER_CALL rejection on the console.
      callWhenReady(() => void webview?.loadURL(url).catch(() => {}));
    },
    [approveHost, setTabUrl, threadRef, webviewFor],
  );

  const submitAddress = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      const normalized = normalizePreviewUrl(addressDraft);
      if (normalized === null || activeTabId === "") {
        return;
      }
      openAddress(activeTabId, normalized);
    },
    [activeTabId, addressDraft, openAddress],
  );

  /**
   * Allowing the site and going there are one act.
   *
   * An agent's request loads into the agent's own tab rather than whichever
   * one is in front, so the next thing the agent looks at is the page it asked
   * for instead of an approval it has no way to notice. A page's blocked
   * navigation resumes in the tab it happened in -- the user may have been
   * browsing that tab themselves, and their page landing in the agent's tab
   * would be the very mix-up this bar exists to avoid.
   */
  const answerPendingApproval = useCallback(
    (decision: BrowserApprovalDecision) => {
      if (pendingApproval === null) {
        return;
      }
      // An agent's request waiting on this question loads the page itself once
      // told; loading it here as well would load it twice.
      if (answerBrowserApproval(threadRef, pendingApproval, decision) === "done") {
        return;
      }
      if (decision === "allowAllSites") {
        setProjectPolicy("any");
      }
      const blockedTabId =
        pendingApproval.tabId !== null &&
        browserState.tabs.some((tab) => tab.id === pendingApproval.tabId)
          ? pendingApproval.tabId
          : null;
      const targetTabId =
        blockedTabId ??
        (agentTabId !== null && browserState.tabs.some((tab) => tab.id === agentTabId)
          ? agentTabId
          : activeTabId);
      if (targetTabId === "") {
        approveHost(pendingApproval.host);
        return;
      }
      openAddress(targetTabId, pendingApproval.url);
    },
    [
      activeTabId,
      agentTabId,
      approveHost,
      browserState.tabs,
      openAddress,
      pendingApproval,
      setProjectPolicy,
      threadRef,
    ],
  );

  /**
   * Which page tool is armed, or null when the pointer belongs to the page.
   *
   * One piece of state for all four: they are the same act of pointing at the
   * page, they all want the pointer, and only one can be live. Kept here rather
   * than in the store because what they act on -- the guest document, and any
   * ink on it -- dies with the page, so a remembered mode would light the
   * control up over nothing.
   */
  const [armedTool, setArmedTool] = useState<PageTool | null>(null);
  // Read inside the pick round trip, which outlives the render that started it.
  const armedToolRef = useRef<PageTool | null>(null);
  const [selectedTool, setSelectedTool] = useState<PageTool>("element");

  const armTool = useCallback(
    async (requested: PageTool | null, options?: { readonly toggle?: boolean }) => {
      const webview = webviewFor(activeTabId);
      if (webview === null || webview === undefined || !isElectron) {
        return;
      }
      const webContentsId = webview.getWebContentsId();
      const previous = armedToolRef.current;
      // Pressing the lit control puts the tool away, which for ink is also how
      // you clear it: putting the pen down and being done are the same moment.
      // Reaching into the menu does not toggle, though -- choosing a tool from
      // a list means use this one, and having it turn the tool off because it
      // happened to already be on reads as the menu being broken.
      const next = options?.toggle !== false && previous === requested ? null : requested;

      if (isInkTool(previous) && !isInkTool(next)) {
        // Parked, not binned. A drawing you have not attached yet is unsent
        // work, and reaching for the picker to name something inside what you
        // just circled should not throw the circle away. Pressing the lit pen
        // is how you clear it, and that comes through here as next === null.
        await window.desktopBridge?.previewSetAnnotationMode?.({
          webContentsId,
          mode: next === null ? null : "idle",
        });
      } else if (previous !== null && !isInkTool(previous)) {
        await window.desktopBridge?.previewCancelPick?.({ webContentsId });
      }

      armedToolRef.current = next;
      setArmedTool(next);
      if (next === null) {
        return;
      }
      if (isInkTool(next)) {
        // Straight to the new mode when moving between the pen and the eraser:
        // the eraser exists to fix the stroke you just drew, and clearing on
        // the way there would leave nothing to fix.
        await window.desktopBridge?.previewSetAnnotationMode?.({ webContentsId, mode: next });
        // One wait covers a stretch with the pen, including trips to the
        // eraser. Coming back after using another tool starts a fresh one,
        // because parking the ink settled the last.
        if (isInkTool(previous)) {
          return;
        }
        const drawing = await window.desktopBridge?.previewAwaitDrawing?.({ webContentsId });
        if (drawing === null || drawing === undefined) {
          return;
        }
        // Photographed before the marks come down, because the ink is half of
        // what the drawing says.
        const shot = await window.desktopBridge?.previewScreenshot?.({ webContentsId });
        if (shot !== undefined && shot.dataUrl.slice(shot.dataUrl.indexOf(",") + 1) !== "") {
          // One thing, because it was one act. The picture and whatever the
          // closed strokes went round travel together behind a single chip
          // rather than scattering across the composer.
          onDrawing?.({
            note: drawing.note,
            imageDataUrl: shot.dataUrl,
            url: activeUrl ?? "",
            // An explicit arrow: map would pass the index into the optional
            // groupId parameter.
            elements: drawing.elements.map((element) => pickedElementFromPreview(element)),
          });
        }
        await window.desktopBridge?.previewSetAnnotationMode?.({ webContentsId, mode: null });
        armedToolRef.current = null;
        setArmedTool(null);
        return;
      }
      try {
        // Annotating is a session, not a shot. Working through a page of
        // feedback means many notes in a row, so attaching one settles it into
        // the composer and re-arms the picker for the next. An empty result is
        // the session ending -- escape with nothing chosen, another tool
        // taking over, or the page navigating away -- and breaks the loop.
        for (;;) {
          const elements = await window.desktopBridge?.previewPickElement?.({
            webContentsId,
            colorScheme: guestColorScheme,
            mode: next,
          });
          // Several elements from one resolution are one annotation sharing
          // one note, and the id is what keeps them together downstream.
          const groupId = (elements?.length ?? 0) > 1 ? crypto.randomUUID() : null;
          for (const element of elements ?? []) {
            onPickElement?.(element, groupId);
          }
          if ((elements?.length ?? 0) === 0 || armedToolRef.current !== next) {
            break;
          }
        }
      } finally {
        // Only if nothing else took the pointer while this was waiting: arming
        // another tool cancels this pick, and its resolution must not then
        // disarm the tool that replaced it.
        if (armedToolRef.current === next) {
          armedToolRef.current = null;
          setArmedTool(null);
        }
      }
    },
    [activeTabId, activeUrl, guestColorScheme, onDrawing, onPickElement],
  );

  // Everything these tools act on lives in the guest document, which a new
  // address replaces. Switching tabs is the same story: the tool was armed on
  // the other one.
  const activeTabUrl = activeTab?.url ?? null;
  useEffect(() => {
    armedToolRef.current = null;
    setArmedTool(null);
  }, [activeTabUrl, activeTabId]);

  // The webview find-in-page is aimed at, fixed at open: the matches live in
  // one document, and the bar closes rather than silently retargeting when
  // the page or the tab changes underneath it.
  const [findTarget, setFindTarget] = useState<PreviewWebview | null>(null);
  useEffect(() => {
    setFindTarget(null);
  }, [activeTabUrl, activeTabId]);

  const openFindBar = useCallback(() => {
    const webview = webviewFor(activeTabId);
    if (webview !== null) {
      setFindTarget(webview);
    }
  }, [activeTabId, webviewFor]);

  // Find and address-bar keys while focus is in the app chrome. Capture
  // phase, because whichever component holds focus (the composer, most of
  // the time) must not swallow a browser shortcut on its way here. Reload
  // and zoom stay with the application menu in the chrome; pressed inside
  // the page they arrive through the command channel below instead.
  useEffect(() => {
    if (!isElectron) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.defaultPrevented) {
        return;
      }
      const key = event.key.toLowerCase();
      if (key === "f") {
        event.preventDefault();
        openFindBar();
      } else if (key === "l") {
        event.preventDefault();
        addressRef.current?.focus();
        addressRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [openFindBar]);

  // Browser gestures the renderer cannot see itself, forwarded by the
  // desktop: the mouse's back/forward buttons (window-level app commands on
  // Windows), and browser shortcuts pressed inside the guest page, which
  // keeps its own keystrokes.
  useEffect(() => {
    const subscribe = window.desktopBridge?.onPreviewBrowserCommand;
    if (subscribe === undefined) {
      return;
    }
    return subscribe((command) => {
      if (command === "find") {
        openFindBar();
        return;
      }
      if (command === "address") {
        addressRef.current?.focus();
        addressRef.current?.select();
        return;
      }
      if (command === "zoom-in" || command === "zoom-out" || command === "zoom-reset") {
        if (activeTab !== null) {
          setTabZoom(
            threadRef,
            activeTab.id,
            command === "zoom-reset"
              ? 1
              : steppedZoom(activeTab.zoomFactor, command === "zoom-in" ? 1 : -1),
          );
        }
        return;
      }
      const webview = webviewFor(activeTabId);
      if (webview === null) {
        return;
      }
      callWhenReady(() => {
        if (command === "reload") {
          webview.reload();
        } else if (command === "back" && webview.canGoBack()) {
          webview.goBack();
        } else if (command === "forward" && webview.canGoForward()) {
          webview.goForward();
        }
      });
    });
  }, [activeTab, activeTabId, openFindBar, setTabZoom, threadRef, webviewFor]);

  // Errors the page has reported since it last navigated, polled while the
  // panel is up. The collection lives in the desktop process alongside the
  // CDP attachment; a light status call every few seconds is what turns it
  // into a visible badge rather than something only the agent ever reads.
  const [pageErrors, setPageErrors] = useState<PageErrorItem[]>([]);
  useEffect(() => {
    setPageErrors([]);
    if (!isElectron || activeTabId === "") {
      return;
    }
    let cancelled = false;
    const poll = async () => {
      const webview = webviewFor(activeTabId);
      const status = window.desktopBridge?.previewStatus;
      if (webview === null || status === undefined) {
        return;
      }
      try {
        const next = await status({ webContentsId: webview.getWebContentsId() });
        if (!cancelled) {
          setPageErrors(distillPageErrors(next));
        }
      } catch {
        // Mid-navigation or not yet attached; the next poll answers.
      }
    };
    void poll();
    const interval = window.setInterval(() => void poll(), 3000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [activeTabId, activeTabUrl, webviewFor]);

  // Escape puts an armed tool away even before the pointer has entered the
  // page: until then, key presses land in the app, not in the guest document
  // where the overlay listens for them. A consumed escape belongs to whatever
  // consumed it -- a menu or dialog closing must not also disarm the tool.
  useEffect(() => {
    if (armedTool === null) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) {
        return;
      }
      event.preventDefault();
      void armTool(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [armedTool, armTool]);

  // A reveal request arrives from the composer, which cannot know which tab is
  // showing the page it came from -- or whether the panel was even open. The
  // panel resolves that here: it prefers a tab already on that URL, falls back
  // to the active one, and reports when the element has since gone.
  const [revealMissing, setRevealMissing] = useState<string | null>(null);
  useEffect(() => {
    if (pendingReveal === null || pendingReveal === undefined || !isElectron) {
      return;
    }
    let cancelled = false;
    const run = async () => {
      const onSameUrl = browserState.tabs.find((tab) => tab.url === pendingReveal.url);
      if (onSameUrl !== undefined && onSameUrl.id !== activeTabId) {
        selectTab(threadRef, onSameUrl.id);
        // Let the newly active tab mount before asking it for anything.
        await new Promise((resolve) => setTimeout(resolve, 120));
      }
      if (cancelled) return;
      const targetId = onSameUrl?.id ?? activeTabId;
      const webview = webviewFor(targetId);
      if (webview === null || webview === undefined) {
        onRevealHandled?.();
        return;
      }
      const found = await window.desktopBridge?.previewRevealElement?.({
        webContentsId: webview.getWebContentsId(),
        selector: pendingReveal.selector,
      });
      if (cancelled) return;
      setRevealMissing(found === true ? null : pendingReveal.id);
      onRevealHandled?.();
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [activeTabId, browserState.tabs, onRevealHandled, pendingReveal, selectTab, threadRef]);

  // The notice clears itself: it answers a question the user just asked and
  // has no meaning a moment later.
  useEffect(() => {
    if (revealMissing === null) {
      return;
    }
    const timer = setTimeout(() => setRevealMissing(null), 4000);
    return () => clearTimeout(timer);
  }, [revealMissing]);

  /**
   * Closing the last tab closes the panel rather than leaving a blank one:
   * clicking close and being handed a fresh tab reads as nothing having
   * happened. The tab is still closed in the store -- which replaces a last
   * tab with a blank one -- because tab state persists per thread, and a page
   * that was closed must not come back the next time the panel opens.
   */
  const closeBrowserTab = useCallback(
    (tabId: string) => {
      closeTab(threadRef, tabId);
      if (browserState.tabs.length <= 1) {
        onClose();
      }
    },
    [browserState.tabs.length, closeTab, onClose, threadRef],
  );

  /**
   * Straight into the composer rather than onto the clipboard.
   *
   * It used to write the data URL to the clipboard as text, which looked like
   * nothing happening and pasted a wall of base64. A screenshot is evidence for
   * the message being written, and everything else picked from the page already
   * attaches itself -- making this one a copy-then-paste errand would be the
   * odd one out.
   */
  /** The camera button briefly becomes a check after a capture attaches.
   *  Deliberately the whole confirmation: a flash over the page read as an
   *  event, and a screenshot landing where you sent it only needs a nod. */
  const [captureConfirmed, setCaptureConfirmed] = useState(false);
  const captureConfirmTimerRef = useRef<number | null>(null);
  useEffect(() => {
    return () => {
      if (captureConfirmTimerRef.current !== null) {
        window.clearTimeout(captureConfirmTimerRef.current);
      }
    };
  }, []);

  const confirmCapture = useCallback(() => {
    setCaptureConfirmed(true);
    if (captureConfirmTimerRef.current !== null) {
      window.clearTimeout(captureConfirmTimerRef.current);
    }
    captureConfirmTimerRef.current = window.setTimeout(() => setCaptureConfirmed(false), 1400);
  }, []);

  /**
   * Where the new-tab button sits: after the last tab while there is room --
   * next to what it acts on, and away from the close-panel control it is not
   * -- docking at the strip's right edge once the tabs overflow, where it
   * would otherwise scroll away with them. The comparison uses a constant for
   * the button's own slot and normalises the viewport to its undocked width,
   * because docking changes both real widths and measuring them would feed
   * the answer back into the question.
   */
  const [newTabDocked, setNewTabDocked] = useState(false);
  const newTabDockedRef = useRef(false);
  const tabsRowRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const tabsRow = tabsRowRef.current;
    const viewport = tabsRow?.closest('[data-slot="scroll-area-viewport"]') ?? null;
    if (tabsRow === null || viewport === null) {
      return;
    }
    const measure = () => {
      const available =
        viewport.clientWidth + (newTabDockedRef.current ? NEW_TAB_BUTTON_SLOT_PX : 0);
      const next = tabsRow.scrollWidth + NEW_TAB_BUTTON_SLOT_PX > available;
      newTabDockedRef.current = next;
      setNewTabDocked(next);
    };
    const observer = new ResizeObserver(measure);
    observer.observe(tabsRow);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, []);

  const captureScreenshot = useCallback(() => {
    const webview = webviewFor(activeTabId);
    if (webview === null || !isElectron) {
      return;
    }
    // Every way this can fail says so. This used to fall through silently on an
    // empty capture or a rejected IPC call, which looked exactly like success
    // until the composer turned out to be missing the image.
    const captureFailed = () => {
      toastManager.add({
        type: "error",
        title: "Screenshot capture failed.",
        description: "The page did not produce an image. Try again.",
      });
    };
    void window.desktopBridge
      ?.previewScreenshot?.({ webContentsId: webview.getWebContentsId() })
      .then((shot) => {
        // A payload-less data URL is still a non-empty string, so the emptiness
        // check has to look past the header.
        if (shot.dataUrl.slice(shot.dataUrl.indexOf(",") + 1) === "") {
          captureFailed();
          return;
        }
        const attached =
          onScreenshot?.({ dataUrl: shot.dataUrl, name: `${screenshotName()}.png` }) ?? false;
        // When the composer declines the image it explains itself (its own
        // toast or thread error), so the panel only confirms, never doubles up.
        if (attached) {
          confirmCapture();
        }
      })
      .catch(captureFailed);
  }, [activeTabId, confirmCapture, onScreenshot, screenshotName]);

  return (
    <section
      className="@container/browser flex min-w-0 flex-col border-l border-border bg-rail"
      style={{ flex: `${flexGrow} 1 0%` }}
      data-testid="browser-panel"
      aria-label="Browser preview"
      onPointerDownCapture={() => {
        markBrowserUserControlled(threadRef);
        // Interaction with the panel is the one kind of click that makes the
        // webview's next focus grab legitimate; see browserFocusGuard.
        noteBrowserUserIntent();
      }}
      onKeyDownCapture={() => {
        markBrowserUserControlled(threadRef);
        noteBrowserUserIntent();
      }}
      onWheelCapture={() => markBrowserUserControlled(threadRef)}
    >
      <div className="flex h-9 shrink-0 items-stretch border-b border-border px-1.5 pt-1.5">
        {/* Tabs scroll; everything after this stays put. ScrollArea keeps the
            strip's height honest: edge mask-fades mark hidden tabs and the
            overlay scrollbar takes no layout height, where a native bar would
            eat into the row and force a vertical one too. */}
        <ScrollArea
          scrollFade
          observeContentResize
          contentClassName="h-full"
          horizontalWheelScroll
          className={cn("min-w-0 flex-1 self-stretch", MINI_HORIZONTAL_SCROLLBAR_CLASS)}
        >
          <div className="flex h-full items-stretch">
            <div ref={tabsRowRef} className="flex h-full items-stretch gap-px">
              {browserState.tabs.map((tab) => (
                <TabStripItem
                  key={tab.id}
                  tab={tab}
                  isActive={tab.id === activeTabId}
                  closable
                  agentState={
                    browserOwnership.agentOwnerByTabId[tab.id] === undefined
                      ? "none"
                      : tab.id === agentTabId && agentActivity?.phase === "running"
                        ? "working"
                        : "pinned"
                  }
                  onSelect={() => selectTab(threadRef, tab.id)}
                  onClose={() => closeBrowserTab(tab.id)}
                />
              ))}
            </div>
            {newTabDocked ? null : <NewTabButton onClick={() => openTab(threadRef)} />}
          </div>
        </ScrollArea>
        {newTabDocked ? <NewTabButton onClick={() => openTab(threadRef)} /> : null}

        {/* Panel-level controls only: where the browser sits and whether it is
            open. What acts on the page lives with the address it acts on. */}
        <div className="flex shrink-0 items-center gap-0.5 self-center">
          <NavButton
            label={expanded ? "Restore chat" : "Expand browser"}
            onClick={toggleExpanded}
            active={expanded}
          >
            {expanded ? (
              <MinimizeIcon className="size-3.5" />
            ) : (
              <MaximizeIcon className="size-3.5" />
            )}
          </NavButton>
          <NavButton label="Close browser" onClick={onClose}>
            <XIcon className="size-3.5" />
          </NavButton>
        </div>
      </div>

      <div className="flex h-11 shrink-0 items-center gap-1.5 border-b border-border px-2">
        <NavButton
          label="Back"
          disabled={!navState.canGoBack}
          onClick={() => activeWebview()?.goBack()}
        >
          <ArrowLeftIcon className="size-3.5" />
        </NavButton>
        <NavButton
          label="Forward"
          disabled={!navState.canGoForward}
          onClick={() => activeWebview()?.goForward()}
        >
          <ArrowRightIcon className="size-3.5" />
        </NavButton>
        <NavButton label="Reload" onClick={() => activeWebview()?.reload()}>
          <RotateCwIcon className={cn("size-3.5", navState.loading && "animate-spin")} />
        </NavButton>

        <form className="relative min-w-0 flex-1" onSubmit={submitAddress}>
          <input
            aria-label="Address"
            data-testid="browser-panel-address"
            className="w-full truncate rounded-md border border-border bg-background py-1 ps-2 pe-7 font-mono text-[11px] text-muted-foreground outline-none focus:border-ring focus:text-foreground"
            placeholder="Search or enter URL"
            ref={addressRef}
            value={addressDraft}
            onChange={(event) => completeAddress(event.target.value)}
            onKeyDown={(event) => {
              // Deleting is a correction. Completing over it would put the
              // guess straight back and make the key look broken.
              deletingRef.current = event.key === "Backspace" || event.key === "Delete";
            }}
          />
          {/* Inside the field, where it reads as "this address, elsewhere"
              rather than as another page control. */}
          <Tooltip>
            <TooltipTrigger
              render={
                <button
                  type="button"
                  aria-label="Open in default browser"
                  data-testid="browser-open-external"
                  disabled={activeUrl === null}
                  className="absolute inset-y-0 end-1 my-auto inline-flex size-5 items-center justify-center rounded text-muted-foreground/60 hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
                  onClick={() => {
                    if (activeUrl !== null) {
                      void window.desktopBridge?.openExternal?.(activeUrl);
                    }
                  }}
                />
              }
            >
              <ExternalLinkIcon className="size-3" />
            </TooltipTrigger>
            <TooltipPopup side="bottom">Open in default browser</TooltipPopup>
          </Tooltip>
        </form>

        {/* Beside the address they act on, which is also where both reference
            browsers put them. */}
        <PageToolControl
          armed={armedTool}
          selected={selectedTool}
          onArm={(tool) => void armTool(tool)}
          onSelect={(tool) => {
            setSelectedTool(tool);
            void armTool(tool, { toggle: false });
          }}
        />
        <BrowserPageErrorsButton url={activeUrl} items={pageErrors} onAttach={onAttachPageErrors} />
        <NavButton
          label={captureConfirmed ? "Screenshot attached" : "Capture screenshot"}
          onClick={captureScreenshot}
          testId="browser-screenshot"
        >
          {captureConfirmed ? (
            <CheckIcon
              className="size-3.5 text-primary-readable"
              data-testid="browser-screenshot-confirmed"
            />
          ) : (
            <CameraIcon className="size-3.5" />
          )}
        </NavButton>
        <Menu>
          <MenuTrigger
            render={
              <button
                type="button"
                aria-label="Browser options"
                data-testid="browser-overflow"
                className="inline-flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground/70 hover:bg-accent hover:text-foreground"
              />
            }
          >
            <MoreVerticalIcon className="size-3.5" />
          </MenuTrigger>
          <MenuPopup align="end">
            <MenuItem data-testid="browser-find-in-page" onClick={openFindBar}>
              Find in page
            </MenuItem>
            <MenuItem
              onClick={() => {
                const webview = webviewFor(activeTabId);
                callWhenReady(() => webview?.reloadIgnoringCache());
              }}
            >
              Hard reload
            </MenuItem>
            <MenuItem data-testid="browser-toggle-device-bar" onClick={toggleDeviceToolbar}>
              {deviceToolbarOpen ? "Hide device toolbar" : "Show device toolbar"}
            </MenuItem>
            <MenuSeparator />
            <MenuRadioGroup
              value={appearance}
              onValueChange={(value) => setAppearance(value as BrowserAppearance)}
            >
              {/* The label belongs to the group: Base UI reads its context, and
                  outside one it throws and takes the whole menu with it. */}
              <MenuGroupLabel>Appearance</MenuGroupLabel>
              {(["system", "light", "dark"] as const).map((value) => (
                <MenuRadioItem key={value} value={value} closeOnClick>
                  {value === "system" ? "Follow app" : value === "light" ? "Light" : "Dark"}
                </MenuRadioItem>
              ))}
            </MenuRadioGroup>
            {approvalsProjectId === null ? null : (
              <>
                <MenuSeparator />
                <MenuRadioGroup
                  value={projectPolicy ?? "default"}
                  onValueChange={(value) =>
                    setProjectPolicy(value === "default" ? null : (value as AgentBrowserSitePolicy))
                  }
                >
                  <MenuGroupLabel>Sites agents can visit here</MenuGroupLabel>
                  <MenuRadioItem value="default" closeOnClick data-testid="browser-sites-default">
                    {`Use default (${defaultPolicy === "any" ? "Any site" : "Ask first"})`}
                  </MenuRadioItem>
                  <MenuRadioItem value="ask" closeOnClick data-testid="browser-sites-ask">
                    Ask first
                  </MenuRadioItem>
                  <MenuRadioItem value="any" closeOnClick data-testid="browser-sites-any">
                    Any site
                  </MenuRadioItem>
                </MenuRadioGroup>
                {access.allowAll || access.approvedHosts.length === 0 ? null : (
                  <MenuSub>
                    <MenuSubTrigger data-testid="browser-allowed-sites">
                      Allowed sites
                    </MenuSubTrigger>
                    <MenuSubPopup className="max-w-64">
                      <MenuGroup>
                        <MenuGroupLabel>Click a site to remove it</MenuGroupLabel>
                        {access.approvedHosts.map((host) => (
                          <MenuItem key={host} onClick={() => removeHost(host)}>
                            <span className="truncate">{host}</span>
                          </MenuItem>
                        ))}
                      </MenuGroup>
                    </MenuSubPopup>
                  </MenuSub>
                )}
              </>
            )}
            <MenuSeparator />
            <MenuItem
              disabled={activeUrl === null}
              onClick={() => {
                if (activeUrl !== null) {
                  void window.desktopBridge?.openExternal?.(activeUrl);
                }
              }}
            >
              Open in default browser
            </MenuItem>
            <MenuItem
              disabled={activeUrl === null}
              onClick={() => {
                if (activeUrl !== null) {
                  void copyTextToClipboard(activeUrl).catch(() => {});
                }
              }}
            >
              Copy address
            </MenuItem>
            <MenuItem
              data-testid="browser-open-devtools"
              onClick={() => {
                const webview = webviewFor(activeTabId);
                const id =
                  webview === null ? null : callWhenReady(() => webview.getWebContentsId());
                if (id !== null) {
                  void window.desktopBridge?.previewOpenDevTools?.({ webContentsId: id });
                }
              }}
            >
              Open developer tools
            </MenuItem>
            <MenuSeparator />
            {/* Both act on this project's profile only. */}
            <MenuItem
              data-testid="browser-clear-cache"
              disabled={partition === null}
              onClick={() => {
                if (partition === null) {
                  return;
                }
                void window.desktopBridge
                  ?.previewClearCache?.({ partition })
                  .then(() => {
                    callWhenReady(() => webviewFor(activeTabId)?.reloadIgnoringCache());
                  })
                  .catch(() => {
                    toastManager.add({ type: "error", title: "Couldn't clear the cache" });
                  });
              }}
            >
              Clear cache
            </MenuItem>
            <MenuItem
              data-testid="browser-clear-data"
              disabled={partition === null}
              onClick={() => {
                if (partition === null) {
                  return;
                }
                // Signing out of the preview is the point, so the desktop
                // reloads every open tab of this project after clearing: a page
                // would otherwise keep looking signed in from memory.
                void window.desktopBridge?.previewClearBrowsingData?.({ partition }).catch(() => {
                  toastManager.add({ type: "error", title: "Couldn't clear cookies and storage" });
                });
              }}
            >
              Clear cookies and storage
            </MenuItem>
          </MenuPopup>
        </Menu>
      </div>

      {/* One agent line, whichever it has to say: a pending question replaces
          the activity that raised it, since "went to X" describes the very
          navigation that was just refused. */}
      {pendingApproval !== null ? (
        <AgentApprovalLine
          host={pendingApproval.host}
          source={pendingApproval.source}
          fromHost={pendingApproval.fromHost}
          waitingCount={pendingApprovals.length - 1}
          onAllow={() => answerPendingApproval("allowSite")}
          onAllowAll={
            approvalsProjectId === null ? undefined : () => answerPendingApproval("allowAllSites")
          }
          onDismiss={() => answerPendingApproval("decline")}
        />
      ) : agentTabId !== null && agentActivity !== null ? (
        <AgentActivityLine
          activity={agentActivity}
          isOnAgentTab={agentTabId === activeTabId}
          onGoToAgentTab={() => selectTab(threadRef, agentTabId)}
        />
      ) : null}

      {deviceToolbarOpen && activeTab !== null ? (
        <DeviceToolbar
          viewport={activeTab.viewport}
          zoomFactor={activeTab.zoomFactor}
          onViewportChange={(viewport) => setTabViewport(threadRef, activeTab.id, viewport)}
          onZoomChange={(factor) => setTabZoom(threadRef, activeTab.id, factor)}
          onClose={toggleDeviceToolbar}
        />
      ) : null}

      {revealMissing === null ? null : (
        <div
          className="shrink-0 border-b border-border bg-amber-500/10 px-3 py-1.5 text-[11px] text-amber-600 dark:text-amber-400"
          data-testid="browser-reveal-missing"
        >
          That element is not on this page any more.
        </div>
      )}
      <div
        className="relative min-h-0 flex-1 overflow-hidden bg-background"
        ref={viewportAreaRef}
        style={{ anchorName: BROWSER_SLOT_ANCHOR } as CSSProperties}
        data-testid="browser-slot"
      >
        {!isElectron ? (
          <BrowserUnavailableNotice
            title="The browser preview needs the desktop app."
            detail="It runs a real Chromium tab so pages can be inspected and driven, which a browser tab cannot host."
          />
        ) : profile.status === "no-project" ? (
          <BrowserUnavailableNotice
            title="The browser needs a project."
            detail="Each project keeps its own sign-ins and site data."
          />
        ) : profile.status === "failed" ? (
          <BrowserUnavailableNotice
            title="The browser couldn't load this project's profile."
            detail="Close the browser and open it again to retry."
          />
        ) : partition === null || overlayElement === null ? null : (
          // Drawn in the layer, over the page: anything rendered in this panel
          // would sit underneath it.
          createPortal(
            <>
              {activeTab !== null && activeTab.url === null ? (
                <div className="pointer-events-auto absolute inset-0 z-10 overflow-auto bg-background px-3">
                  <LocalServerPicker
                    onSelect={(port) => openAddress(activeTab.id, `http://localhost:${port}`)}
                  />
                </div>
              ) : null}
              {findTarget === null ? null : (
                <div className="pointer-events-auto">
                  <BrowserFindBar webview={findTarget} onClose={() => setFindTarget(null)} />
                </div>
              )}
            </>,
            overlayElement,
          )
        )}
      </div>
    </section>
  );
}

/** The new-tab button's footprint in the strip: its size-6 box plus lead-in
 *  margin. A constant rather than a measurement, so the docking decision never
 *  depends on where the button currently is. */
const NEW_TAB_BUTTON_SLOT_PX = 26;

function NewTabButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label="New tab"
      data-testid="browser-new-tab"
      className="ms-0.5 inline-flex size-6 shrink-0 items-center justify-center self-center rounded-md text-muted-foreground/70 hover:bg-accent hover:text-foreground"
      onClick={onClick}
    >
      <PlusIcon className="size-3.5" />
    </button>
  );
}

function TabStripItem({
  tab,
  isActive,
  closable,
  agentState,
  onSelect,
  onClose,
}: {
  tab: BrowserTab;
  isActive: boolean;
  closable: boolean;
  /** Whether the agent is working in this tab, and whether right now. */
  agentState: "none" | "pinned" | "working";
  onSelect: () => void;
  onClose: () => void;
}) {
  const label = tab.title ?? (tab.url === null ? "New tab" : tab.url.replace(/^https?:\/\//, ""));
  // Tabs rehydrated from storage predating the field arrive without it.
  const faviconUrl = tab.faviconUrl ?? null;
  // A favicon that fails to load falls back to the globe rather than a broken
  // image. Keyed by URL so a later navigation gets to try again.
  const [failedFaviconUrl, setFailedFaviconUrl] = useState<string | null>(null);
  const favicon = faviconUrl !== null && faviconUrl !== failedFaviconUrl ? faviconUrl : null;
  return (
    <div
      className={cn(
        "group/tab flex min-w-0 max-w-44 shrink-0 items-center gap-1.5 rounded-t-md px-2 text-xs",
        // A hairline under the agent's tab, so it is findable at a glance
        // without competing with which tab is selected.
        agentState !== "none" && "border-b-2 border-primary-readable",
        isActive
          ? "bg-background text-foreground"
          : "text-muted-foreground/80 hover:bg-accent hover:text-foreground",
      )}
      data-testid={`browser-tab-${tab.id}`}
    >
      <button
        type="button"
        className="flex min-w-0 flex-1 items-center gap-1.5 py-1"
        onClick={onSelect}
        title={label}
      >
        {/* A dot rather than a badge: the tab still has to read as a tab, and
            this only needs to answer "is the agent in here". It pulses while a
            call is in flight and holds steady between them. */}
        {agentState === "none" ? (
          favicon !== null ? (
            <img
              src={favicon}
              alt=""
              aria-hidden
              className="size-3 shrink-0"
              onError={() => setFailedFaviconUrl(favicon)}
            />
          ) : (
            <GlobeIcon className="size-3 shrink-0 opacity-70" />
          )
        ) : (
          <span
            aria-label={agentState === "working" ? "Agent working here" : "The agent's tab"}
            data-testid={`browser-tab-agent-${tab.id}`}
            className={cn(
              "size-1.5 shrink-0 rounded-full bg-primary-readable",
              agentState === "working" && "animate-pulse",
            )}
          />
        )}
        <span className="min-w-0 truncate">{label}</span>
      </button>
      {closable ? (
        <button
          type="button"
          aria-label={`Close ${label}`}
          data-testid="browser-close-tab"
          className="inline-flex size-4 shrink-0 items-center justify-center rounded opacity-0 hover:bg-accent hover:text-foreground group-hover/tab:opacity-100"
          onClick={onClose}
        >
          <XIcon className="size-3" />
        </button>
      ) : null}
    </div>
  );
}

/**
 * The four ways of pointing at the page, behind one control.
 *
 * Four buttons in a row read as four unrelated things and crowd the address
 * out of a panel that is often narrow. They are one gesture aimed differently,
 * so they get one slot: the tool you last chose, and a caret to change it.
 * Choosing from the menu arms it too -- reaching for a tool is the same act as
 * picking it up.
 */
// One word each. The menu is a list of four things that differ only in what
// they point at, and a sentence apiece made the card wider than the panel it
// hangs off without saying more than the icon already does.
const PAGE_TOOLS = [
  { tool: "element", label: "Annotate", Icon: MousePointerClickIcon },
  { tool: "region", label: "Region", Icon: SquareDashedMousePointerIcon },
  { tool: "draw", label: "Draw", Icon: PencilIcon },
  { tool: "erase", label: "Erase", Icon: EraserIcon },
] as const satisfies ReadonlyArray<{ tool: PageTool; label: string; Icon: typeof PencilIcon }>;

type PageTool = DesktopPreviewPickMode | DesktopPreviewAnnotationMode;

/** Ink is armed and cleared differently from a pick, and survives the swap. */
function isInkTool(tool: PageTool | null): tool is DesktopPreviewAnnotationMode {
  return tool === "draw" || tool === "erase";
}

function PageToolControl({
  armed,
  selected,
  onArm,
  onSelect,
}: {
  armed: PageTool | null;
  selected: PageTool;
  onArm: (tool: PageTool) => void;
  onSelect: (tool: PageTool) => void;
}) {
  // The armed tool wins over the selected one: while something is live the
  // control has to show what the pointer is actually doing.
  const shown = PAGE_TOOLS.find((entry) => entry.tool === (armed ?? selected)) ?? PAGE_TOOLS[0];
  const isArmed = armed !== null;

  // A mouse press must not leave keyboard focus on the arm button -- its
  // pointerdown is prevented below, so only tabbing focuses it. The menu
  // trigger cannot get the same treatment (Base UI owns its pointer
  // handling), so disarming also sheds any focus left inside the control:
  // an escape press would promote that ring to visible on a control nobody
  // is using. The blur lands before paint so the ring never flashes.
  const rootRef = useRef<HTMLSpanElement | null>(null);
  useLayoutEffect(() => {
    if (armed !== null) {
      return;
    }
    const active = document.activeElement;
    if (active instanceof HTMLElement && rootRef.current?.contains(active)) {
      active.blur();
    }
  }, [armed]);
  const label = isArmed ? (isInkTool(armed) ? "Clear drawing" : "Cancel") : shown.label;

  return (
    <span
      ref={rootRef}
      className={cn(
        "inline-flex shrink-0 items-center rounded-md border",
        isArmed ? "border-transparent bg-accent text-foreground" : "border-border",
      )}
      data-testid="browser-page-tool"
    >
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              type="button"
              aria-label={label}
              data-testid="browser-page-tool-arm"
              onPointerDown={(event) => event.preventDefault()}
              onClick={() => onArm(shown.tool)}
              className={cn(
                "inline-flex size-6 items-center justify-center rounded-l-md hover:bg-accent hover:text-foreground",
                isArmed ? "text-foreground" : "text-muted-foreground/70",
              )}
            />
          }
        >
          <shown.Icon className="size-3.5" />
        </TooltipTrigger>
        <TooltipPopup side="bottom">{label}</TooltipPopup>
      </Tooltip>
      <span className={cn("h-3.5 w-px", isArmed ? "bg-border/60" : "bg-border")} />
      <Menu>
        <MenuTrigger
          render={
            <button
              type="button"
              aria-label="Choose a page tool"
              data-testid="browser-page-tool-menu"
              className={cn(
                "inline-flex h-6 w-4 items-center justify-center rounded-r-md hover:bg-accent hover:text-foreground",
                isArmed ? "text-foreground" : "text-muted-foreground/70",
              )}
            />
          }
        >
          <ChevronDownIcon className="size-3" />
        </MenuTrigger>
        <MenuPopup align="end">
          <MenuRadioGroup value={selected} onValueChange={(value) => onSelect(value as PageTool)}>
            {PAGE_TOOLS.map((entry) => (
              <MenuRadioItem
                key={entry.tool}
                value={entry.tool}
                variant="fill"
                data-testid={`browser-page-tool-${entry.tool}`}
              >
                {/* Flexed because the reset makes an svg a block, which would
                    otherwise drop the label onto its own line. */}
                <span className="flex items-center gap-2">
                  <entry.Icon className="size-3.5 text-muted-foreground/70" />
                  {entry.label}
                </span>
              </MenuRadioItem>
            ))}
          </MenuRadioGroup>
        </MenuPopup>
      </Menu>
    </span>
  );
}

/**
 * The one question the browser ever asks you, in the agent's own line.
 *
 * An agent may reach any private address it likes -- your dev server is the
 * whole point of the panel -- but the open internet is somewhere you decide it
 * goes. The question wears the activity line's grammar ("Agent wants to visit"
 * beside "Agent went to") because it is the same narration, paused: an amber
 * dot instead of a second bar, and the two answers where "show" would sit.
 *
 * The subject is whoever actually navigated. Saying "Agent" for a page's own
 * redirect or link would accuse it of something the user did -- the exact
 * confusion between your browsing and the agent's this bar is meant to keep
 * straight.
 *
 * Both answers are remembered for this project and reach no further, which the
 * buttons' titles say: allowing a docs site once does not quietly allow it
 * everywhere you work, and "Allow all sites" switches only this project to
 * "Any site". An agent waiting on the answer carries on by itself.
 */
export function AgentApprovalLine({
  host,
  source,
  fromHost,
  waitingCount = 0,
  onAllow,
  onAllowAll,
  onDismiss,
}: {
  host: string;
  source: "agent" | "page";
  fromHost: string | null;
  /** Further questions queued behind this one. */
  waitingCount?: number;
  onAllow: () => void;
  /** Absent when there is no project to remember the answer for. */
  onAllowAll?: (() => void) | undefined;
  onDismiss: () => void;
}) {
  const subject = source === "agent" ? "Agent" : (fromHost ?? "This page");
  return (
    <div
      className="flex h-[22px] w-full shrink-0 items-center gap-1.5 border-b border-border px-2 font-mono text-[11px] text-muted-foreground"
      data-testid="browser-approval-bar"
    >
      <span className="size-1.5 shrink-0 animate-pulse rounded-full bg-amber-500" />
      <span className="min-w-0 truncate">
        <span className="text-foreground">{subject}</span> wants to visit {host}
      </span>
      {waitingCount > 0 ? (
        <span className="shrink-0 text-muted-foreground/60" data-testid="browser-approval-more">
          +{waitingCount} more
        </span>
      ) : null}
      <button
        type="button"
        data-testid="browser-approval-allow"
        title="Remembered for this project"
        className="ms-auto shrink-0 text-amber-600/70 hover:text-amber-600 dark:text-amber-400/70 dark:hover:text-amber-400"
        onClick={onAllow}
      >
        Allow site
      </button>
      {onAllowAll === undefined ? null : (
        <button
          type="button"
          data-testid="browser-approval-allow-all"
          title="Lets agents visit any site in this project"
          className="shrink-0 text-amber-600/70 hover:text-amber-600 dark:text-amber-400/70 dark:hover:text-amber-400"
          onClick={onAllowAll}
        >
          Allow all sites
        </button>
      )}
      <button
        type="button"
        data-testid="browser-approval-dismiss"
        className="shrink-0 text-muted-foreground/60 hover:text-foreground"
        onClick={onDismiss}
      >
        Not now
      </button>
    </div>
  );
}

/**
 * What the agent is doing, in one line under the toolbar.
 *
 * The tab dot says where; this says what. Without it the only evidence an
 * agent is working is the page changing on its own, which reads the same as
 * the page being broken.
 *
 * Clicking it goes to the agent's tab, which is the whole follow affordance --
 * a mode that moved your selection for you would be the yanking this was built
 * to stop.
 */
export function AgentActivityLine({
  activity,
  isOnAgentTab,
  onGoToAgentTab,
}: {
  activity: AgentActivity;
  isOnAgentTab: boolean;
  onGoToAgentTab: () => void;
}) {
  const body = (
    <>
      <span
        className={cn(
          "size-1.5 shrink-0 rounded-full bg-primary-readable",
          activity.phase === "running" && "animate-pulse",
        )}
      />
      <span className="shrink-0 text-foreground">Agent</span>
      <span className="min-w-0 truncate">
        {activity.verb}
        {activity.detail === null ? "" : ` ${activity.detail}`}
      </span>
    </>
  );
  const className =
    "flex h-[22px] w-full shrink-0 items-center gap-1.5 border-b border-border px-2 font-mono text-[11px] text-muted-foreground";

  if (isOnAgentTab) {
    return (
      <div className={className} data-testid="agent-activity-line">
        {body}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onGoToAgentTab}
      title="Go to the tab the agent is working in"
      data-testid="agent-activity-line"
      className={cn(className, "text-left hover:bg-accent hover:text-foreground")}
    >
      {body}
      <span className="ms-auto shrink-0 text-muted-foreground/60">show</span>
    </button>
  );
}

function NavButton({
  label,
  disabled,
  active,
  onClick,
  testId,
  children,
}: {
  label: string;
  disabled?: boolean;
  active?: boolean;
  onClick: () => void;
  testId?: string;
  children: React.ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            aria-label={label}
            {...(testId === undefined ? {} : { "data-testid": testId })}
            disabled={disabled}
            onClick={onClick}
            className={cn(
              "inline-flex size-6 shrink-0 items-center justify-center rounded-md hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-40",
              active === true ? "bg-accent text-foreground" : "text-muted-foreground/70",
            )}
          />
        }
      >
        {children}
      </TooltipTrigger>
      <TooltipPopup side="bottom">{label}</TooltipPopup>
    </Tooltip>
  );
}

/**
 * What is listening right now, offered as destinations.
 *
 * A blank address bar is a worse starting point than it looks: the port a dev
 * server picked is exactly the thing nobody remembers. Rows rather than tiles,
 * separated by rules, so it reads as a list of facts.
 */
function LocalServerPicker({ onSelect }: { onSelect: (port: number) => void }) {
  const [servers, setServers] = useState<ReadonlyArray<DesktopLocalServer>>([]);
  const [scanned, setScanned] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const scan = () => {
      void window.desktopBridge?.previewLocalServers?.().then((found) => {
        if (!cancelled) {
          setServers(found);
          setScanned(true);
        }
      });
    };
    scan();
    // Servers start and stop while the panel is open; a stale list would send
    // the user to a port that has since died.
    const interval = window.setInterval(scan, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  return (
    <div className="mx-auto w-full max-w-md py-8">
      <div className="mb-2 flex items-center gap-2 px-2 text-muted-foreground">
        <RadioTowerIcon className="size-4" />
        <span className="text-sm">Local servers</span>
      </div>
      {servers.length === 0 ? (
        <p className="px-2 py-6 text-xs text-muted-foreground/70">
          {scanned ? "Nothing is listening right now." : "Looking for local servers…"}
        </p>
      ) : (
        <div className="flex flex-col border-t border-border/60">
          {servers.map((server) => (
            <button
              key={server.port}
              type="button"
              data-testid={`local-server-${server.port}`}
              className="flex items-center gap-3 rounded-md border-b border-border/60 px-2 py-2.5 text-left hover:bg-accent"
              onClick={() => onSelect(server.port)}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-foreground">
                  {server.title ??
                    (server.processName === "" ? "Unknown server" : server.processName)}
                </span>
                <span className="block truncate font-mono text-[11px] text-muted-foreground/70">
                  localhost:{server.port}
                </span>
              </span>
              <span className="size-1.5 shrink-0 rounded-full bg-success" />
            </button>
          ))}
        </div>
      )}
      <p className="px-2 pt-3 text-xs text-muted-foreground/60">
        Select a listening port to open it here.
      </p>
    </div>
  );
}

/**
 * Why the panel has no page to show, in place of the tabs.
 *
 * The preview is a Chromium <webview>, which only exists in the desktop app.
 * Rather than degrade to an iframe -- which cannot be driven, inspected, or
 * navigated cross-origin -- the web build says so plainly. The desktop says so
 * too when a thread has no project to give it a browser profile, rather than
 * putting its pages in some other project's.
 */
function BrowserUnavailableNotice({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
      <ExternalLinkIcon className="size-5 text-muted-foreground/40" />
      <p className="text-sm text-muted-foreground">{title}</p>
      <p className="max-w-xs text-xs text-muted-foreground/70">{detail}</p>
    </div>
  );
}

/**
 * Size and zoom for the page under test.
 *
 * Hidden until asked for, because sizing is an occasional task and a permanent
 * control costs toolbar width on every page you ever look at. Width and height
 * are editable rather than a fixed menu of devices: the presets fill them in,
 * and any other size is reachable by typing it.
 */
function DeviceToolbar({
  viewport,
  zoomFactor,
  onViewportChange,
  onZoomChange,
  onClose,
}: {
  viewport: BrowserViewport;
  zoomFactor: number;
  onViewportChange: (viewport: BrowserViewport) => void;
  onZoomChange: (factor: number) => void;
  onClose: () => void;
}) {
  const matchingPreset = BROWSER_VIEWPORT_PRESETS.find(
    (entry) => entry.width === viewport.width && entry.height === viewport.height,
  );
  const selectedMode = matchingPreset?.label ?? "Custom";

  return (
    <div
      className="flex h-9 shrink-0 items-center justify-center gap-1.5 overflow-x-auto border-b border-border px-2"
      data-testid="browser-device-toolbar"
    >
      <select
        aria-label="Device"
        data-testid="browser-device-preset"
        className="min-w-0 max-w-28 shrink rounded-md border border-border bg-background px-1 py-1 text-[11px] text-muted-foreground outline-none focus:border-ring"
        value={selectedMode}
        onChange={(event) => {
          const preset = BROWSER_VIEWPORT_PRESETS.find((p) => p.label === event.target.value);
          if (preset !== undefined) {
            onViewportChange({ width: preset.width, height: preset.height });
          }
        }}
      >
        {matchingPreset === undefined ? (
          <option value="Custom" disabled>
            Custom
          </option>
        ) : null}
        {BROWSER_VIEWPORT_PRESETS.map((preset) => (
          <option key={preset.label} value={preset.label}>
            {preset.label}
          </option>
        ))}
      </select>

      <DimensionInput
        label="Width"
        value={viewport.width}
        onCommit={(width) =>
          onViewportChange(
            width === null ? RESPONSIVE_VIEWPORT : { width, height: viewport.height ?? 800 },
          )
        }
      />
      <span className="text-muted-foreground/50">×</span>
      <DimensionInput
        label="Height"
        value={viewport.height}
        onCommit={(height) =>
          onViewportChange(
            height === null ? RESPONSIVE_VIEWPORT : { width: viewport.width ?? 1280, height },
          )
        }
      />

      <button
        type="button"
        aria-label="Rotate"
        data-testid="browser-device-rotate"
        disabled={viewport.width === null}
        className="hidden size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground/70 hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-40 @xs/browser:inline-flex"
        onClick={() => onViewportChange({ width: viewport.height, height: viewport.width })}
      >
        <RotateDeviceIcon className="size-3.5" />
      </button>

      <div className="hidden shrink-0 items-center gap-1 @sm/browser:flex">
        <button
          type="button"
          aria-label="Zoom out"
          className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground/70 hover:bg-accent hover:text-foreground"
          onClick={() => onZoomChange(steppedZoom(zoomFactor, -1))}
        >
          −
        </button>
        <button
          type="button"
          aria-label="Reset zoom"
          data-testid="browser-zoom-value"
          className="min-w-9 shrink-0 rounded-md px-0.5 font-mono text-[10px] text-muted-foreground hover:bg-accent hover:text-foreground"
          onClick={() => onZoomChange(1)}
        >
          {Math.round(zoomFactor * 100)}%
        </button>
        <button
          type="button"
          aria-label="Zoom in"
          className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground/70 hover:bg-accent hover:text-foreground"
          onClick={() => onZoomChange(steppedZoom(zoomFactor, 1))}
        >
          +
        </button>
      </div>

      <button
        type="button"
        aria-label="Hide device toolbar"
        className="inline-flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground/70 hover:bg-accent hover:text-foreground"
        onClick={onClose}
      >
        <XIcon className="size-3.5" />
      </button>
    </div>
  );
}

/** Empty means "let it fill", which is how you get back to responsive by typing. */
function DimensionInput({
  label,
  value,
  onCommit,
}: {
  label: string;
  value: number | null;
  onCommit: (value: number | null) => void;
}) {
  const [draft, setDraft] = useState(value === null ? "" : String(value));
  useEffect(() => {
    setDraft(value === null ? "" : String(value));
  }, [value]);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed === "") {
      onCommit(null);
      return;
    }
    const parsed = Number.parseInt(trimmed, 10);
    onCommit(Number.isNaN(parsed) || parsed < 1 ? value : Math.min(parsed, 9999));
  };

  return (
    <input
      aria-label={label}
      data-testid={`browser-device-${label.toLowerCase()}`}
      inputMode="numeric"
      className="w-12 shrink-0 rounded-md border border-border bg-background px-1 py-1 text-center font-mono text-[11px] text-muted-foreground outline-none focus:border-ring focus:text-foreground"
      placeholder="auto"
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          commit();
        }
      }}
    />
  );
}
